import DXFWriter from '@tarikjabiri/dxf';
import { Island, type Vertex2 } from '@rta/rta-blueprint-library';

import { addVectors } from './add-vector';
import { applyPanelItemMasks } from './apply-panel-item-masks';
import { convertRect2Vectors } from '@r-core/helpers';
import { generateCountertopVectors } from './generate-countertop-vectors';
import { generatePanelVectors } from './generate-panel-vectors';
import { PADDING_BETWEEN_ELEMENTS } from './constants';
import { splitVectorBreakpoints } from './split-vector-breakpoints';
import { Layer, type Vector } from '@r-core/types';

export type IslandRowResult = { vectors: Array<Vector<Vertex2>>; rowHeight: number; rowWidth: number };

export type RenderIslandRowParams = {
	drawing: DXFWriter;
	island: Island;
	rowYOffset: number;
};

/**
 * Renders a single island row: countertop (top view) on left, panels (front view) on right
 */
export const renderIslandRow = (params: RenderIslandRowParams): IslandRowResult => {
	const { drawing, island, rowYOffset } = params;
	const countertop = island.getCountertop();
	const { islandSize } = island;

	let currentX = islandSize.width / 2;

	const countertopResult = generateCountertopVectors({
		drawing,
		countertop,
		island,
		xOffset: currentX,
		yOffset: rowYOffset
	});

	let allVectors: Array<Vector<Vertex2>> = countertopResult.vectors;

	const rawCountertopBreakpoints = [...countertop.breakpoints.children].sort((a, b) => a.center - b.center).map((bp) => bp.center);

	island.panels.children.forEach((panel) => {
		allVectors = applyPanelItemMasks({
				panel,
				xOffset: currentX + panel.center.x,
				yOffset: rowYOffset + panel.center.z,
				allVectors,
				isCountertop: true,
				applyMask: true
			});
	});

	allVectors = splitVectorBreakpoints(allVectors, currentX, rawCountertopBreakpoints).vectors;

	// Move to right edge of countertop, then add padding
	currentX = countertopResult.width + PADDING_BETWEEN_ELEMENTS;

	// Render panels in front view (x, y coordinates)
	// For front view, we use yOffset for the y position in DXF
	const panelYOffset = rowYOffset;
	let maxPanelHeight = 0;

	if (island.panels.children && island.panels.children.length > 0) {
		island.panels.children.forEach((panel) => {
			const panelWidth = panel.size.width;
			const panelCenterX = currentX + (panelWidth / 2);

			const panelResult = generatePanelVectors({
				drawing,
				panel,
				xOffset: panelCenterX,
				yOffset: panelYOffset
			});

			// Add panel items (appliances)
			const panelVectors = applyPanelItemMasks({
				panel,
				xOffset: panelCenterX,
				yOffset: panelYOffset,
				allVectors: panelResult.vectors,
				counterThickness: countertop.material.thickness,
				applyMask: true
			});

			allVectors = [
				...allVectors,
				...splitVectorBreakpoints(panelVectors, panelCenterX, [...panel.breakpoints.children].sort((a, b) => a.center - b.center).map((bp) => bp.center)).vectors
			]

			// allVectors = addVectors(panel.label, {
			// 	vectors: allVectors,
			// 	newVectors: panelVectors
			// });

			currentX += panelResult.width + PADDING_BETWEEN_ELEMENTS;
			maxPanelHeight = Math.max(maxPanelHeight, panelResult.height);
		});
	}

	// Calculate row dimensions
	const rowHeight = Math.max(countertopResult.height, maxPanelHeight);
	const rowWidth = currentX - PADDING_BETWEEN_ELEMENTS; // Remove last padding

	return { vectors: allVectors, rowHeight, rowWidth };
};

